import { forwardRef, RefObject, useCallback, useImperativeHandle, useState } from 'react'
import { CALENDAR_MODE } from '@/configs'
import dayjs from '@/lib/dayjs'
import FullCalendar from '@fullcalendar/react'
import { Button, Typography } from '@mui/material'
import { Box } from '@mui/system'
import ChevronLeftRoundedIcon from '@mui/icons-material/ChevronLeftRounded'
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded'

interface CalendarDateFilterProps {
  calendarMode: CALENDAR_MODE
  calendarRef: RefObject<FullCalendar | null>
  onUpdate?: (time: { startTime: string; endTime: string }) => void
}

export interface ExposeCalendarDateFilterProps {
  updateElement: (mode: CALENDAR_MODE) => void
}

export const CalendarDateFilter = forwardRef<
  ExposeCalendarDateFilterProps,
  CalendarDateFilterProps
>(({ calendarRef, calendarMode, onUpdate }, ref) => {
  const [title, setTitle] = useState('')

  const updateElement = useCallback((mode: CALENDAR_MODE) => {
    const calendarApi = calendarRef.current?.getApi()
    if (calendarApi) {
      const view = calendarApi.view
      const isDifferentMonth = !dayjs(view.currentStart).isSame(view.currentEnd, 'month')
      const isDifferentYear = !dayjs(view.currentStart).isSame(view.currentEnd, 'year')
      const title =
        mode === CALENDAR_MODE.DAILY
          ? `${dayjs(view.currentStart).format('D MMMM YYYY')}`
          : mode === CALENDAR_MODE.WEEKLY
            ? `${dayjs(view.currentStart).format(isDifferentYear ? 'D MMMM YYYY' : isDifferentMonth ? 'D MMMM' : 'D')} - ${dayjs(view.currentEnd).add(-1, 'day').format('D MMMM YYYY')}`
            : `${dayjs(view.currentStart).format('D')} - ${dayjs(view.currentStart).endOf('month').format('D MMMM YYYY')}`
      setTitle(title)

      switch (mode) {
        case CALENDAR_MODE.DAILY:
          onUpdate?.({
            startTime: dayjs(view.currentStart).startOf('day').toISOString(),
            endTime: dayjs(view.currentStart).endOf('day').toISOString(),
          })
          break
        case CALENDAR_MODE.WEEKLY:
          onUpdate?.({
            startTime: dayjs(view.currentStart).startOf('week').toISOString(),
            endTime: dayjs(view.currentStart).endOf('week').toISOString(),
          })
          break
        case CALENDAR_MODE.LIST:
        case CALENDAR_MODE.MONTHLY:
          onUpdate?.({
            startTime: dayjs(view.currentStart).startOf('month').toISOString(),
            endTime: dayjs(view.currentStart).endOf('month').toISOString(),
          })
          break
      }
    }
  }, [])

  const handlePrev = () => {
    calendarRef.current?.getApi().prev()
    updateElement(calendarMode)
  }

  const handleNext = () => {
    calendarRef.current?.getApi().next()
    updateElement(calendarMode)
  }

  useImperativeHandle(ref, () => ({ updateElement }), [updateElement])

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        background: (theme) =>
          theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.06)' : '#f3f3f3',
        color: 'text.primary',
        height: 60,
        borderRadius: '16px',
      }}
    >
      <Button
        variant="text"
        sx={{ minWidth: 60, height: '100%', p: 0, borderRadius: '16px 0 0 16px' }}
        onClick={handlePrev}
      >
        <ChevronLeftRoundedIcon color="secondary" sx={{ fontSize: 24 }} />
      </Button>
      <Typography variant="subtitle1">{title}</Typography>
      <Button
        variant="text"
        sx={{ minWidth: 60, height: '100%', p: 0, borderRadius: '0 16px 16px 0' }}
        onClick={handleNext}
      >
        <ChevronRightRoundedIcon color="secondary" sx={{ fontSize: 24 }} />
      </Button>
    </Box>
  )
})
